import { X } from '@phosphor-icons/react';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { REGISTER_UPSELL_VARIANT, REGISTER_UPSELL_VARIANT_BY_CONTEXT } from '../../../const';
import { useIsDesktopViewport } from '../../../hooks/useIsDesktopViewport';
import {
    clearAuthError,
    getAuthFlow,
    getAuthModalClosingPossibility,
    getAuthModalContext,
    getAuthModalState,
    getRegisterStepCount,
    toggleAuthModal
} from '../../../store/slices/authSlice';
import { setOverflowForSidebar, setPageHiddenBehindModal } from '../../../utils/helper';
import Modal from '../../common/Modal';
import LogoIcon from '../../icons/LogoIcon';
import FirstAuthStep from './FirstAuthStep';
import styles from './authModal.module.scss';

// HORO-10509: SecondAuthStep ("Hi, {name}! Add birth details for more
// accurate readings and guidance") is temporarily disabled — not in use
// right now. Uncomment the import and the entry below to restore it.
// import SecondAuthStep from './SecondAuthStep';

// const stepsConfig = [FirstAuthStep, SecondAuthStep];
const stepsConfig = [FirstAuthStep];

// HORO-10509: Sign in and Enter password are their own full-height views
// (no card, no dimmed backdrop) using a `position: fixed` + internal
// overflow-y:auto container — that works fine for them (short/medium
// content, tested working on real devices). Sign up initially used the
// same fixed-container approach, but on real iOS Safari it broke on
// keyboard open: `dvh` doesn't always recompute promptly around
// focus/blur, and combined with the fixed container's own scroll, the
// email field's keyboard could reveal a gap of real page content
// underneath — the screen visibly acting like a modal floating over the
// page rather than being the page. Sign up now renders as a normal
// (non-fixed) full-bleed block instead — see `.signUpScreen` — so the
// real document scrolls it (and its hero image, which lives in-flow
// inside EntryStep.js rather than as a separate fixed background layer),
// which is the one scroll mechanism iOS's keyboard-avoidance handles
// natively and reliably.
const STARFIELD_FLOWS = ['sign-in', 'set-password', 'verify-email', 'forgot-password', 'reset-password'];

// HORO-10509: desktop-specific centered modal card (Figma "Registration
// screen_general_desktop" / "Registeration_general" / "Welcome back"
// desktop) — Sign up, Set password, Sign in, and now Verify email all use
// it (confirmed via Figma — "Verify your email" has its own desktop card
// frame, same 753x573 card/hero treatment as the others). Reuses the
// exact same hero photo (`signup_hero.jpg`) as its right-column image,
// per Jonny/Sanjhal's Figma comment thread on that frame: full image, no
// crop, blended into the card background with CSS rather than made a
// literal "background image" layer.
//
// forgot-password/reset-password added here too (Figma 4542:50168
// desktop "Reset your password", 4542:52114/4690:8442 desktop "Set a new
// password") — same card/hero treatment, confirmed via Figma rather than
// carried over by assumption. Previously these two rendered as the old
// centered `Modal` below, same class of bug the Verify-email screen had
// before it was added to this list.
const DESKTOP_CARD_FLOWS = ['entry', 'set-password', 'sign-in', 'verify-email', 'forgot-password', 'reset-password'];

const AuthModal = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const isShow = useSelector(getAuthModalState);
    const registerStep = useSelector(getRegisterStepCount);
    const isCanBeClosed = useSelector(getAuthModalClosingPossibility);
    const authFlow = useSelector(getAuthFlow);
    const authModalContext = useSelector(getAuthModalContext);
    const isStarfieldFlow = STARFIELD_FLOWS.includes(authFlow);
    const isDesktop = useIsDesktopViewport();
    // HORO-10495: the desktop card's shared hero image (below) only varies
    // by entry-point copy variant during the 'entry'/sign-up flow — every
    // other flow sharing this card (sign-in, set-password, ...) always uses
    // the general hero, regardless of what context opened the modal.
    const registerVariant = REGISTER_UPSELL_VARIANT_BY_CONTEXT[authModalContext] || REGISTER_UPSELL_VARIANT.GENERAL;
    const isTarotDesktopHero = authFlow === 'entry' && registerVariant === REGISTER_UPSELL_VARIANT.TAROT;

    const Form = useMemo(() => {
        // Falls back to FirstAuthStep if registerStep is ever 2 (SecondAuthStep
        // disabled above) so nothing tries to render an undefined component.
        return stepsConfig[registerStep - 1] || FirstAuthStep;
    }, [registerStep]);

    const onBtnClose = () => {
        dispatch(toggleAuthModal({ trigger: false, step: registerStep }));
    };

    useEffect(() => {
        dispatch(clearAuthError());
    }, []);

    // HORO-10509: the user should never be able to see anything of the real
    // page (catalog, chat, whatever) while any auth screen is up — but the
    // mobile screens (`.signUpScreen`/`.fullScreenAuth`) overlay the still-
    // present page rather than replacing it, and that page's own content
    // keeps contributing to the document's scrollable height underneath
    // them. Scroll far enough — either because the page was already
    // scrolled down when the modal opened, or the user keeps scrolling once
    // it's up — and there's nothing left of the auth screen to cover it,
    // so the real page shows through below. `setPageHiddenBehindModal`
    // (utils/helper.js) removes the page from layout entirely for as long
    // as that's true, which structurally prevents ever scrolling into it —
    // there's simply nothing there to scroll into.
    //
    // Desktop's card flow is the deliberate exception: its backdrop is a
    // blurred/dimmed view of the real page (`.authDesktopScrim`), not a
    // solid replacement, so the page has to stay in layout and visible
    // there. `setOverflowForSidebar` locks *its* scroll instead, so it
    // can't be silently scrolled behind the dimmed card.
    const isDesktopCardFlow = isShow && isDesktop && DESKTOP_CARD_FLOWS.includes(authFlow);
    const hidePageBehindModal = isShow && !isDesktopCardFlow;
    // `.signUpScreen` specifically (mobile "entry"/Sign up) is also
    // `position: absolute; top: 0` rather than `fixed` (see that class's
    // comment in authModal.module.scss for why) — it's pinned to the
    // *document's* top, not the current viewport. Hiding the real page
    // above shrinks the document, but browsers clamp an out-of-range
    // scroll position down to whatever's left rather than resetting it to
    // 0 — which for a scrolled-down page would land this screen scrolled
    // to its *own* bottom instead of its top. Forcing it to 0 for exactly
    // this flow is what actually lines it up.
    const isMobileSignUpScreen = isShow && !isDesktop && authFlow === 'entry';
    const savedScrollY = useRef(0);

    useEffect(() => {
        if (hidePageBehindModal) {
            // Captured before hiding the page — hiding it can itself
            // clamp/shift the current scroll position (see above), so this
            // has to run first to save the position the user actually left
            // the page at.
            savedScrollY.current = window.scrollY;
        }
        setPageHiddenBehindModal(hidePageBehindModal);
        if (isMobileSignUpScreen) {
            // `<html>` has global `scroll-behavior: smooth` (general.scss);
            // `behavior: 'instant'` overrides that so this is an immediate
            // jump rather than a brief visible scroll animation as the
            // modal opens.
            window.scrollTo({ top: 0, behavior: 'instant' });
        }
        setOverflowForSidebar(isDesktopCardFlow);

        return () => {
            setPageHiddenBehindModal(false);
            setOverflowForSidebar(false);
            if (hidePageBehindModal) {
                window.scrollTo({ top: savedScrollY.current, behavior: 'instant' });
            }
        };
    }, [hidePageBehindModal, isDesktopCardFlow, isMobileSignUpScreen]);

    // HORO-10509: `.fullScreenAuth`'s min/max-height falls back to `dvh`,
    // which is supposed to already track the visible viewport (shrinking
    // for the keyboard, growing back when it's dismissed) — but real iOS
    // Safari doesn't always re-run that calc promptly on focus/blur, and
    // its auto-hiding address bar changes the truly-visible area on top of
    // that. Net effect: after the keyboard opens then closes (or the
    // address bar shows/hides) while scrolled down, the box can end up
    // sized for a viewport that no longer matches reality, stranding
    // content below the fold with no way to scroll to it. `visualViewport`
    // is the one API that reliably reports the actual current visible
    // height in both cases, so mirror it into a CSS var `.fullScreenAuth`
    // prefers over `dvh` whenever it's available. Only Sign in/Enter
    // password still use that fixed container — Sign up moved off it
    // entirely (see the comment above STARFIELD_FLOWS).
    useEffect(() => {
        if (!isStarfieldFlow || typeof window === 'undefined' || !window.visualViewport) {
            return undefined;
        }

        const root = document.documentElement;
        const setVar = () => {
            root.style.setProperty('--auth-viewport-height', `${window.visualViewport.height}px`);
        };

        setVar();
        window.visualViewport.addEventListener('resize', setVar);
        window.visualViewport.addEventListener('scroll', setVar);

        return () => {
            window.visualViewport.removeEventListener('resize', setVar);
            window.visualViewport.removeEventListener('scroll', setVar);
            root.style.removeProperty('--auth-viewport-height');
        };
    }, [isStarfieldFlow]);

    if (!isShow) {
        return null;
    }

    if (isDesktop && DESKTOP_CARD_FLOWS.includes(authFlow)) {
        return (
            <div className={styles.authDesktopBackdrop}>
                <div className={styles.authDesktopScrim} aria-hidden="true" />
                <div className={styles.authDesktopCard}>
                    <div className={styles.authDesktopLeft}>
                        <Form />
                    </div>
                    <div
                        className={
                            isTarotDesktopHero ? `${styles.authDesktopRight} ${styles.authDesktopRightTarot}` : styles.authDesktopRight
                        }
                    >
                        <div className={styles.authDesktopHeader}>
                            <LogoIcon />
                        </div>
                        {/* Verify-email is the one desktop-card flow with no
                            way back in without it: closing here strands a
                            registered-but-unverified account with no path to
                            finish. Every other card flow (sign in, set
                            password, forgot/reset password) can still be
                            dismissed normally. */}
                        {authFlow !== 'verify-email' && (
                            <button
                                type="button"
                                className={styles.authDesktopCloseBtn}
                                aria-label={t('buttons.close')}
                                onClick={onBtnClose}
                            >
                                <X size={20} color="#FFFFFF" />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    if (authFlow === 'entry') {
        // Normal document flow (not `position: fixed`) — see the comment
        // above STARFIELD_FLOWS for why. The hero image lives inside
        // EntryStep.js itself, in-flow, so it scrolls with everything else.
        //
        // HORO-10509: the fade-in animation lives on `.signUpScreenContent`
        // now, not this outer div — this outer div's own opacity used to
        // animate 0→1 too, which meant its solid `#080911` background was
        // *also* transparent for that first ~250ms, letting whatever page
        // sits underneath (chat, the advisor carousel) show through. This
        // div mounts fully opaque immediately; only the content inside
        // fades in against that already-solid backdrop.
        return (
            <div className={styles.signUpScreen}>
                <div className={styles.signUpScreenContent}>
                    <Form />
                </div>
            </div>
        );
    }

    if (isStarfieldFlow) {
        return (
            <div className={styles.fullScreenAuth}>
                {/* Starfield + fade-to-black + progressive blur, per Figma
                    (see authModal.module.scss for the exact values). Fixed
                    behind the form content, not part of its scroll. */}
                <div className={styles.authBackground} aria-hidden="true">
                    <div className={styles.authBgImage} />
                    <div className={styles.authBgFade} />
                    <div className={styles.authBgBlur1} />
                    <div className={styles.authBgBlur2} />
                    <div className={styles.authBgBlur3} />
                    <div className={styles.authBgBlur4} />
                    <div className={styles.authBgBlur5} />
                </div>
                {/* Same reasoning as `.signUpScreenContent` above — see its
                    comment. */}
                <div className={styles.fullScreenAuthContent}>
                    <Form />
                </div>
            </div>
        );
    }

    return <Modal children={<Form />} withCrossBtn={true} onCloseClick={isCanBeClosed ? onBtnClose : null} />;
};

export default AuthModal;
